// helpers/deleteManuallyAddedProductHelper.ts

import axios, { AxiosError } from 'axios';

export interface DeleteManualProductInTcBody {
    project: number;
    userId: number;
    productId: number;
}

export interface DeleteManualProductInTcResponse {
    success: boolean;
    message: string;
    productId: number;
}

const TC_DELETE_ERROR_MESSAGES: Record<number, string> = {
    400: 'Missing required fields when deleting product from catalog.',
    403: 'This product does not belong to you in the catalog.',
    404: 'Product not found in the catalog — it may already be deleted.',
    500: 'Catalog failed to save the deletion. Please try again.',
};

/**
 * Marks a manually-added product as deleted in the Transparency Catalog.
 * Calls TC's action route directly via a bare axios call — deliberately NOT
 * using the app's shared http() wrapper/axios instance, since that prepends
 * our own API base URL and attaches our own bearer token, neither of which
 * apply to this endpoint (no auth header is specified in the TC delete spec).
 * Does NOT delete the local Project_Product row — that still happens via the
 * existing deleteProductAction. Callers must fire both.
 */
export async function deleteManualProductInTc(
    body: DeleteManualProductInTcBody,
): Promise<DeleteManualProductInTcResponse> {
    try {
        const response = await axios.post<DeleteManualProductInTcResponse>(
            `${process.env.REACT_APP_TC_URL}/actions/project-builder/products/delete`,
            body,
            {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            },
        );
        return response.data;
    } catch (error) {
        const axiosError = error as AxiosError<{ message?: string }>;
        const status = axiosError.response?.status;
        const message = status
            ? TC_DELETE_ERROR_MESSAGES[status] || axiosError.response?.data?.message
            : undefined;
        throw new Error(message || 'Failed to delete product from catalog.');
    }
}
